import {t, tpl} from '../../i18n/i18n.js';
import {CompositionGrid} from '../../core/vocabulary.js';

/**
 * DOM builders for rows in the section editor: the frame shown above two blocks that share a
 * row, its split bar, and the block picker used to add a block below a card. The builders hold
 * no composition state; the section editor wires the callbacks and calls `update` after every
 * change.
 */

const percent = (span) => Math.round((span / CompositionGrid.COLUMNS) * 100);

/**
 * Marks `element` with the colour of its shared row as `data-row-tone`, or clears the mark when
 * `tone` is undefined (a block alone in its row stays neutral).
 *
 * @param {HTMLElement} element
 * @param {number | undefined} tone a colour index from `rowTones`
 */
export function setRowTone(element, tone) {
    if (tone === undefined) delete element.dataset.rowTone;
    else element.dataset.rowTone = String(tone);
}

/**
 * A bar with the two blocks of a row as segments and a divider between them. The divider is a
 * slider in grid columns: pointer drag and the arrow, Home and End keys call `onChange` with a
 * whole number of columns for the first block, clamped to the bounds of the last `update`, and
 * only when that differs from the current split.
 *
 * @param {{onChange: (span: number) => void}} options
 * @returns {{element: HTMLDivElement, update: (view: {firstLabel: string, secondLabel: string, span: number, min: number, max: number, rowNumber: number}) => void}}
 */
export function createSplitBar({onChange}) {
    const element = document.createElement('div');
    element.className = 'split-bar';
    const track = document.createElement('div');
    track.className = 'split-bar-track';
    const first = document.createElement('span');
    first.className = 'split-bar-segment split-bar-segment--first';
    const handle = document.createElement('span');
    handle.className = 'split-bar-handle';
    handle.tabIndex = 0;
    handle.setAttribute('role', 'slider');
    const second = document.createElement('span');
    second.className = 'split-bar-segment split-bar-segment--second';
    track.append(first, handle, second);
    const readout = document.createElement('span');
    readout.className = 'split-bar-readout';
    element.append(track, readout);

    let view = {span: CompositionGrid.COLUMNS / 2, min: 1, max: CompositionGrid.COLUMNS - 1};
    /** Calls `onChange` with the clamped span, only when it differs from the current one. */
    const change = (span) => {
        const clamped = Math.min(view.max, Math.max(view.min, Math.round(span)));
        if (clamped !== view.span) onChange(clamped);
    };

    handle.addEventListener('keydown', (event) => {
        const steps = {ArrowLeft: view.span - 1, ArrowDown: view.span - 1, ArrowRight: view.span + 1, ArrowUp: view.span + 1, Home: view.min, End: view.max};
        if (!(event.key in steps)) return;
        event.preventDefault();
        change(steps[event.key]);
    });

    /**
     * Attaches the listeners for one pointer drag and returns a function that removes them all.
     * Called for `pointerup`, `pointercancel` and `lostpointercapture` alike, so a drag that ends
     * without a matching `pointerup` (the pointer leaves the window, another app steals capture,
     * ...) still cleans up instead of leaving a stray `pointermove` listener behind.
     */
    function attachDrag() {
        const move = (moveEvent) => {
            const rect = track.getBoundingClientRect();
            change(((moveEvent.clientX - rect.left) / rect.width) * CompositionGrid.COLUMNS);
        };
        const end = () => {
            handle.removeEventListener('pointermove', move);
            handle.removeEventListener('pointerup', end);
            handle.removeEventListener('pointercancel', end);
            handle.removeEventListener('lostpointercapture', end);
        };
        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', end);
        handle.addEventListener('pointercancel', end);
        handle.addEventListener('lostpointercapture', end);
        return end;
    }

    /** Ends the previous drag's listeners, when a pointerdown ever arrives without one. */
    let endDrag = null;
    handle.addEventListener('pointerdown', (event) => {
        endDrag?.();
        handle.setPointerCapture(event.pointerId);
        endDrag = attachDrag();
    });

    function update(next) {
        view = next;
        const rest = CompositionGrid.COLUMNS - next.span;
        first.textContent = next.firstLabel;
        second.textContent = next.secondLabel;
        first.style.flexGrow = String(next.span);
        second.style.flexGrow = String(rest);
        readout.textContent = tpl('pageForm.rowSplitValue', {first: percent(next.span), second: percent(rest)});
        handle.setAttribute('aria-label', tpl('pageForm.rowSplit', {n: next.rowNumber}));
        handle.setAttribute('aria-valuemin', String(next.min));
        handle.setAttribute('aria-valuemax', String(next.max));
        handle.setAttribute('aria-valuenow', String(next.span));
        handle.setAttribute('aria-valuetext', `${next.firstLabel} ${percent(next.span)}%, ${next.secondLabel} ${percent(rest)}%`);
    }

    return {element, update};
}

/**
 * The frame shown above the two cards of a shared row: its title, split bar, a slot for the row
 * background choice, and the swap and unpair actions.
 *
 * @param {{onSplit: (span: number) => void, onSwap: () => void, onDissolve: () => void}} options
 * @returns {{element: HTMLLIElement, bandSlot: HTMLDivElement, update: (view: {rowNumber: number, firstLabel: string, secondLabel: string, span: number, min: number, max: number}) => void}}
 */
export function createRowFrame({onSplit, onSwap, onDissolve}) {
    const element = document.createElement('li');
    element.className = 'section-row-frame';
    const title = document.createElement('p');
    title.className = 'section-row-title';
    const bar = createSplitBar({onChange: onSplit});
    const bandSlot = document.createElement('div');
    bandSlot.className = 'section-row-band';
    const actions = document.createElement('div');
    actions.className = 'section-row-actions';
    const swap = document.createElement('button');
    swap.type = 'button';
    swap.className = 'btn btn-sm btn-outline section-row-swap';
    swap.dataset.i18n = 'pageForm.rowSwap';
    swap.textContent = t('pageForm.rowSwap');
    swap.addEventListener('click', onSwap);
    const dissolve = document.createElement('button');
    dissolve.type = 'button';
    dissolve.className = 'btn btn-sm btn-outline section-row-dissolve';
    dissolve.dataset.i18n = 'pageForm.rowDissolve';
    dissolve.textContent = t('pageForm.rowDissolve');
    dissolve.addEventListener('click', onDissolve);
    actions.append(swap, dissolve);
    element.append(title, bar.element, bandSlot, actions);

    function update(view) {
        title.textContent = tpl('pageForm.rowTitle', {n: view.rowNumber});
        bar.update(view);
    }

    return {element, bandSlot, update};
}

/**
 * Inline choice of the block type to add below a card: one button per repeatable section, and a
 * cancel button. Escape cancels.
 *
 * @param {{id: string, sections: import('../../shell/types/user.types.js').CompositionSection[], onPick: (sectionId: string) => void, onCancel: () => void}} options
 * @returns {HTMLDivElement}
 */
export function createBlockPicker({id, sections, onPick, onCancel}) {
    const picker = document.createElement('div');
    picker.className = 'section-block-picker';
    picker.setAttribute('role', 'group');
    picker.setAttribute('aria-labelledby', id);
    picker.hidden = true;
    const question = document.createElement('p');
    question.className = 'section-block-picker-question';
    question.id = id;
    question.dataset.i18n = 'pageForm.addBelowQuestion';
    question.textContent = t('pageForm.addBelowQuestion');
    const options = document.createElement('div');
    options.className = 'section-block-picker-options';
    for (const section of sections) {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'btn btn-sm btn-outline section-block-picker-option';
        option.dataset.sectionId = section.id;
        option.textContent = section.repeatable.itemLabel || section.label;
        option.addEventListener('click', () => onPick(section.id));
        options.appendChild(option);
    }
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'btn btn-sm section-block-picker-cancel';
    cancel.dataset.i18n = 'pageForm.addBelowCancel';
    cancel.textContent = t('pageForm.addBelowCancel');
    cancel.addEventListener('click', onCancel);
    picker.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') onCancel();
    });
    picker.append(question, options, cancel);
    return picker;
}
